import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ChannelItem, ChannelItemType } from 'types/channel';
import type { UseChannelContentManageResult } from '../../../hooks/useChannelContentManage';
import DropdownAtom from '../../atoms/Dropdown/DropdownAtom';
import ConfirmDialogAtom from '../../atoms/ConfirmDialog/ConfirmDialogAtom';
import DraggableItemListMolecule from '../../molecules/marketplace/DraggableItemListMolecule';
import { ItemDetailDialogOrganism } from './ItemDetailDialogOrganism';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import './ChannelItemsSectionOrganism.scss';

const ITEM_TYPE_LABEL_KEYS: Record<ChannelItemType, string> = {
  approach: 'marketplace.channelManage.v3Items.typeApproach',
  surface_template: 'marketplace.channelManage.v3Items.typeSurfaceTemplate',
  assistant: 'marketplace.channelManage.v3Items.typeAssistant',
  video: 'marketplace.channelManage.v3Items.typeVideo',
  form: 'marketplace.channelManage.v3Items.typeForm',
  metaspace: 'marketplace.channelManage.v3Items.typeMetaspace',
  schema: 'marketplace.channelManage.v3Items.typeSchema',
  context: 'marketplace.channelManage.v3Items.typeContext',
  arc_template: 'marketplace.channelManage.v3Items.typeArcTemplate',
  orbit_template: 'marketplace.channelManage.v3Items.typeOrbitTemplate',
  aspect: 'marketplace.channelManage.v3Items.typeAspect',
  aspect_group: 'marketplace.channelManage.v3Items.typeAspectGroup',
  composition: 'marketplace.channelManage.v3Items.typeComposition',
  archetype: 'marketplace.channelManage.v3Items.typeArchetype',
};

// Order the filter chips. `ITEM_TYPE_LABEL_KEYS`/`ITEM_TYPE_ICONS` above are
// exhaustive `Record`s, so a new `ChannelItemType` fails the build there
// before it could silently fall off the end of this ordering array.
const ITEM_TYPE_ORDER: ChannelItemType[] = [
  'approach',
  'surface_template',
  'assistant',
  'video',
  'form',
  'metaspace',
  'schema',
  'context',
  'arc_template',
  'orbit_template',
  'aspect',
  'aspect_group',
  'composition',
  'archetype',
];

// PrimeIcon per content type, shown on the filter chips (ChipAtom `icon`).
const ITEM_TYPE_ICONS: Record<ChannelItemType, string> = {
  approach: 'pi pi-clipboard',
  surface_template: 'pi pi-th-large',
  assistant: 'pi pi-android',
  video: 'pi pi-video',
  form: 'pi pi-file-edit',
  metaspace: 'pi pi-chart-line',
  schema: 'pi pi-database',
  context: 'pi pi-sitemap',
  arc_template: 'pi pi-directions',
  orbit_template: 'pi pi-refresh',
  aspect: 'pi pi-tag',
  aspect_group: 'pi pi-tag',
  composition: 'pi pi-objects-column',
  archetype: 'pi pi-tags',
};

const SORT_OPTIONS = ['manual', 'dateAdded', 'name', 'type'] as const;
type SortOption = (typeof SORT_OPTIONS)[number];

export interface ChannelItemsSectionOrganismProps {
  channelId: string;
  canManage: boolean;
  contentManage: UseChannelContentManageResult;
  onAddItem?: () => void;
  onSyndicateItem?: () => void;
  className?: string;
}

const ChannelItemsSectionOrganism: React.FC<ChannelItemsSectionOrganismProps> = ({
  channelId,
  canManage,
  contentManage,
  onAddItem,
  className = '',
}) => {
  const { t } = useTranslation();
  const { items, loading, error, reorder, isReordering, isRemoving, remove, refetch } =
    contentManage;

  const [activeFilter, setActiveFilter] = useState<ChannelItemType | 'all'>('all');
  const [sortBy, setSortBy] = useState<SortOption>('manual');
  const [selectedItem, setSelectedItem] = useState<ChannelItem | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<ChannelItem | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );

  const publishedItems = useMemo(
    () => items.filter((it) => it.item_status === 'published' || !it.item_status),
    [items]
  );
  const draftItems = useMemo(
    () => items.filter((it) => it.item_status === 'draft'),
    [items]
  );

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: publishedItems.length };
    publishedItems.forEach((it) => {
      counts[it.item_type] = (counts[it.item_type] ?? 0) + 1;
    });
    return counts;
  }, [publishedItems]);

  const presentTypes = useMemo(
    () => ITEM_TYPE_ORDER.filter((type) => (typeCounts[type] ?? 0) > 0),
    [typeCounts]
  );

  const filteredItems = useMemo(
    () =>
      activeFilter === 'all'
        ? publishedItems
        : publishedItems.filter((it) => it.item_type === activeFilter),
    [activeFilter, publishedItems]
  );

  // Non-manual sorts produce a derived view; manual keeps the persisted order
  // (and is the only mode where drag-reorder is allowed).
  const sortedItems = useMemo(() => {
    if (sortBy === 'manual') return filteredItems;
    const arr = [...filteredItems];
    if (sortBy === 'dateAdded') {
      arr.sort(
        (a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime()
      );
    } else if (sortBy === 'name') {
      arr.sort((a, b) =>
        getLocalizedText(a.item_metadata?.name)
          .toLowerCase()
          .localeCompare(getLocalizedText(b.item_metadata?.name).toLowerCase())
      );
    } else if (sortBy === 'type') {
      arr.sort((a, b) => a.item_type.localeCompare(b.item_type));
    }
    return arr;
  }, [filteredItems, sortBy]);

  const sortOptions = useMemo(
    () =>
      SORT_OPTIONS.map((value) => ({
        value,
        label: t(`marketplace.channelManage.content.sortOptions.${value}`),
      })),
    [t]
  );

  const handleItemClick = useCallback((item: ChannelItem, rowElement: HTMLElement) => {
    rowElement.focus();
    setSelectedItem(item);
    setDialogVisible(true);
  }, []);

  const handleReorder = useCallback(
    async (itemIds: string[]) => {
      setFeedback(null);
      const ok = await reorder(itemIds);
      setFeedback(
        ok
          ? { type: 'success', message: t('marketplace.channelManage.content.reorderSuccess') }
          : { type: 'error', message: t('marketplace.channelManage.content.reorderError') }
      );
    },
    [reorder, t]
  );

  const handleRemoveClick = useCallback((item: ChannelItem) => {
    setRemoveTarget(item);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removeTarget) return;
    setFeedback(null);
    const ok = await remove(removeTarget.id);
    setRemoveTarget(null);
    setFeedback(
      ok
        ? { type: 'success', message: t('marketplace.channelManage.content.removeSuccess') }
        : { type: 'error', message: t('marketplace.channelManage.content.removeError') }
    );
  }, [removeTarget, remove, t]);

  const classes = ['channel-items-section', className].filter(Boolean).join(' ');

  if (loading) {
    return (
      <div className={classes}>
        <span className="marketplace-marketplace__spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes}>
        <p className="channel-items-section__error">
          {t('marketplace.channelManage.content.loadError')}
        </p>
        <button type="button" onClick={refetch}>
          {t('marketplace.common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className={classes}>
      {/* Remove confirmation — accessible (ConfirmDialogAtom) + published-aware copy */}
      <ConfirmDialogAtom
        visible={!!removeTarget}
        variant="danger"
        message={
          removeTarget?.item_status === 'draft'
            ? t('marketplace.channelManage.content.removeConfirm')
            : t('marketplace.channelManage.content.removeConfirmPublished')
        }
        acceptLabel={t('marketplace.channelManage.content.removeItem')}
        rejectLabel={t('marketplace.channelManage.settings.cancelButton')}
        accept={handleRemoveConfirm}
        reject={() => setRemoveTarget(null)}
        onHide={() => setRemoveTarget(null)}
      />

      {/* Items card */}
      <div className="channel-items-section__card">
        <div className="channel-items-section__card-header">
          <div className="channel-items-section__card-heading">
            <h2 className="channel-items-section__card-title">
              {t('marketplace.channelManage.v3Items.sectionTitle')}
            </h2>
            <p className="channel-items-section__card-subtitle">
              {t('marketplace.channelManage.v3Items.sectionSubtitle')}
            </p>
          </div>
          {canManage && onAddItem && (
            <button
              type="button"
              className="channel-items-section__add-btn"
              onClick={onAddItem}
            >
              <i className="pi pi-plus" aria-hidden="true" />
              {t('marketplace.channelManage.v3Items.newItem')}
            </button>
          )}
        </div>

        {/* Type filter pills */}
        <div className="channel-items-section__filters">
          <button
            type="button"
            className={`channel-items-section__filter-pill ${activeFilter === 'all' ? 'channel-items-section__filter-pill--active' : ''}`}
            onClick={() => setActiveFilter('all')}
            data-testid="items-filter-pill-all"
          >
            {t('marketplace.channelManage.v3Items.filterAll')} {typeCounts['all'] ?? 0}
          </button>
          {presentTypes.map((type) => {
            const labelKey = ITEM_TYPE_LABEL_KEYS[type];
            const iconClass = ITEM_TYPE_ICONS[type];
            if (!labelKey) return null;
            return (
              <button
                key={type}
                type="button"
                className={`channel-items-section__filter-pill ${activeFilter === type ? 'channel-items-section__filter-pill--active' : ''}`}
                onClick={() => setActiveFilter(type)}
                data-testid={`items-filter-pill-${type}`}
              >
                {iconClass && <i className={`${iconClass} channel-items-section__filter-pill-icon`} aria-hidden="true" />}
                {t(labelKey)} {typeCounts[type] ?? 0}
              </button>
            );
          })}
          {publishedItems.length > 0 && (
            <DropdownAtom
              className="channel-items-section__sort"
              dropdownSize="small"
              value={sortBy}
              options={sortOptions}
              onChange={(e) => setSortBy(e.value as SortOption)}
              ariaLabel={t('marketplace.channelManage.content.sortLabel')}
            />
          )}
        </div>

        {feedback && (
          <p
            className={`channel-items-section__feedback channel-items-section__feedback--${feedback.type}`}
          >
            {feedback.message}
          </p>
        )}

        {filteredItems.length === 0 ? (
          <div className="channel-items-section__empty">
            <i className="pi pi-box channel-items-section__empty-icon" aria-hidden="true" />
            <p className="channel-items-section__empty-title">
              {activeFilter === 'all'
                ? t('marketplace.channelManage.v3Items.noItemsYet')
                : t('marketplace.channelManage.v3Items.noTypeItems')}
            </p>
            <p className="channel-items-section__empty-hint">
              {t('marketplace.channelManage.v3Items.noItemsHint')}
            </p>
          </div>
        ) : (
          <DraggableItemListMolecule
            items={sortedItems}
            onReorder={handleReorder}
            onRemove={handleRemoveClick}
            onItemClick={handleItemClick}
            disabled={
              activeFilter !== 'all' ||
              sortBy !== 'manual' ||
              !canManage ||
              isReordering ||
              isRemoving
            }
          />
        )}
      </div>

      {/* Drafts subsection */}
      <div className="channel-items-section__card">
        <div className="channel-items-section__card-header">
          <div className="channel-items-section__card-heading">
            <h2 className="channel-items-section__card-title">
              {t('marketplace.channelManage.v3Items.draftsSectionTitle')}
            </h2>
            <p className="channel-items-section__card-subtitle">
              {t('marketplace.channelManage.v3Items.draftsSectionSubtitle')}
            </p>
          </div>
        </div>

        {draftItems.length === 0 ? (
          <div className="channel-items-section__drafts-empty">
            <i
              className="pi pi-check-circle channel-items-section__drafts-icon"
              aria-hidden="true"
            />
            <span className="channel-items-section__drafts-label">
              {t('marketplace.channelManage.v3Items.allPublished')}
            </span>
          </div>
        ) : (
          <DraggableItemListMolecule
            items={draftItems}
            onReorder={handleReorder}
            onRemove={handleRemoveClick}
            onItemClick={handleItemClick}
            disabled={true}
          />
        )}
      </div>

      {selectedItem && (
        <ItemDetailDialogOrganism
          item={selectedItem}
          channelId={channelId}
          visible={dialogVisible}
          permissions={{ canView: true, canManage, canDeploy: false, canFork: false }}
          onHide={() => setDialogVisible(false)}
          onItemUpdated={refetch}
        />
      )}
    </div>
  );
};

ChannelItemsSectionOrganism.displayName = 'ChannelItemsSectionOrganism';

export default ChannelItemsSectionOrganism;
